import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, CheckCircle, FileText, ArrowRight, Thermometer, Wind } from 'lucide-react';

const GeothermalPermitting = () => {
  const permitTypes = [
    {
      type: 'Air Quality Permits',
      description: 'Permits for emissions during drilling, testing, and potential production operations',
      requirements: ['H2S monitoring and control', 'Particulate matter control', 'VOC emissions management'],
    },
    {
      type: 'Testing Phase Permits',
      description: 'Temporary permits for exploration and testing activities',
      requirements: ['Short-term emission limits', 'Stack testing requirements', 'Reporting protocols'],
    },
  ];

  const emissionSources = [
    {
      source: 'Drilling Operations',
      pollutants: 'Diesel particulate matter, NOx, CO',
      controls: 'Low-emission drilling equipment, dust suppression',
    },
    {
      source: 'Well Testing',
      pollutants: 'H2S, CO2, VOCs, particulate matter',
      controls: 'H2S abatement systems, monitoring equipment',
    },
    {
      source: 'Support Equipment',
      pollutants: 'Combustion emissions from generators and pumps',
      controls: 'Tier 4 engines, emission controls',
    },
  ];

  const alaskaConsiderations = [
    'Remote location operational challenges',
    'Winter drilling conditions and emissions',
    'Wildlife and habitat protection requirements',
    'Proximity to sensitive areas (parks, wilderness)',
    'Tribal consultation requirements',
    'Baseline air quality monitoring',
  ];

  return (
    <div className="min-h-screen" data-testid="geothermal-page">
      {/* Hero Section */}
      <section
        className="hero-section relative text-white overflow-hidden"
        style={{
          height: '60vh',
          maxHeight: '720px',
          minHeight: '600px',
          padding: '5rem 0'
        }}
      >
        {/* Responsive Hero Image Container */}
        <div className="absolute inset-0 z-0">
          <style>{`
            .hero-section picture {
              width: 100%;
              height: 100%;
              display: block;
            }
            
            .hero-section .hero-banner-image,
            .hero-section picture img {
              width: 100% !important;
              height: 100% !important;
              object-fit: cover !important;
              object-position: center bottom !important;
              display: block !important;
            }
            
            @media (max-width: 900px) {
              .hero-section {
                height: auto !important;
                max-height: none !important;
                min-height: 420px !important;
                padding: 3rem 0 !important;
              }
              .hero-section picture img {
                object-position: center center !important;
              }
            }
          `}</style>
          
          <picture>
            <source
              media="(min-width: 1536px)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/vtsaqraj_geothermal_hero_desktop_2560x1200.webp"
              type="image/webp" />

            <source
              media="(min-width: 900px)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/dv3ss8lj_geothermal_hero_tablet_1536x900.webp"
              type="image/webp" />

            <source
              media="(orientation: landscape)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/ti1uyz4j_geothermal_hero_mobile_horizontal_960x640.webp"
              type="image/webp" />

            <img
              src="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/qh7atyrb_geothermal_hero_mobile_vertical_960x1200.webp"
              alt="Geothermal test well permitting — high-resolution image of an active geothermal drilling installation with pipes, steam risers, equipment pads, and surrounding rugged terrain, Alaska"
              loading="eager"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }}
              className="hero-banner-image w-full h-full" />
          </picture>
          
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 h-full flex items-center">
          <div className="max-w-3xl">
            <div className="flex items-center mb-6">
              <Zap className="w-16 h-16 mr-4 drop-shadow-lg" />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold drop-shadow-lg">
                  Geothermal Test Well Permitting
                </h1>
                <p className="text-xl mt-2 drop-shadow-md">Environmental Compliance for Renewable Energy</p>
              </div>
            </div>
            <p className="text-xl md:text-2xl max-w-3xl drop-shadow-md">
              Expert permitting assistance for geothermal exploration and test well operations in Alaska.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Alaska's Geothermal Potential</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Alaska possesses significant geothermal energy potential, with numerous hot springs and volcanic features throughout the state. Geothermal test well drilling represents a crucial step in assessing the viability of geothermal resources for power generation and direct heating applications.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Raven Environmental Consulting provides comprehensive permitting support for geothermal exploration projects, ensuring compliance with all federal and state environmental regulations while facilitating the development of this clean, renewable energy source.
            </p>
            
            <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-600 flex items-start">
              <Thermometer className="w-8 h-8 text-purple-600 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Renewable Energy Development</h3>
                <p className="text-gray-700">
                  Geothermal energy offers Alaska communities a reliable, renewable energy source that can reduce dependence on diesel fuel in remote areas while minimizing environmental impact.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Permit Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Required Environmental Permits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {permitTypes.map((permit, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-purple-600">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{permit.type}</h3>
                <p className="text-gray-600 mb-6">{permit.description}</p>
                <h4 className="font-semibold text-gray-900 mb-3">Key Requirements:</h4>
                <ul className="space-y-2">
                  {permit.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emission Sources */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Emission Sources & Control Measures</h2>
          <div className="space-y-6 max-w-5xl mx-auto">
            {emissionSources.map((source, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-lg shadow-lg border-l-4 border-purple-600">
                <div className="flex items-start mb-4">
                  <Wind className="w-8 h-8 text-purple-600 mr-4 flex-shrink-0" />
                  <h3 className="text-2xl font-bold text-gray-900">{source.source}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Pollutants</h4>
                    <p className="text-gray-600">{source.pollutants}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Control Measures</h4>
                    <p className="text-gray-600">{source.controls}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Requirements */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Federal & State Regulatory Requirements</h2>
          <div className="space-y-8 max-w-5xl mx-auto">
            {/* NSPS */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 60 - New Source Performance Standards</h3>
              <p className="text-gray-600 mb-4">Applicable standards for geothermal operations:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart IIII</h4>
                  <p className="text-sm text-gray-600">Compression ignition engines for drilling and support operations</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart JJJJ</h4>
                  <p className="text-sm text-gray-600">Spark ignition engines for ancillary equipment</p>
                </div>
              </div>
            </div>

            {/* H2S Management */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Hydrogen Sulfide (H2S) Management</h3>
              <p className="text-gray-600 mb-4">
                Geothermal fluids often contain H2S, requiring special management:
              </p>
              <div className="space-y-3">
                <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Ambient Air Quality Standards</h4>
                    <p className="text-sm text-gray-600">Compliance with Alaska's H2S ambient standards (42 ppb 1-hour, 9 ppb 24-hour)</p>
                  </div>
                </div>
                <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Abatement Systems</h4>
                    <p className="text-sm text-gray-600">Installation of H2S removal or conversion systems when necessary</p>
                  </div>
                </div>
                <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Continuous Monitoring</h4>
                    <p className="text-sm text-gray-600">Real-time H2S monitoring during well testing operations</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alaska Specific */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-purple-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Alaska-Specific Considerations</h3>
              <p className="text-gray-600 mb-4">
                Unique factors for geothermal development in Alaska:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {alaskaConsiderations.map((consideration, index) => (
                  <div key={index} className="flex items-start bg-gray-50 p-4 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{consideration}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Permitting Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Permitting Process</h2>
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Initial Assessment</h4>
                    <p className="text-gray-600">Evaluate project scope, location, and regulatory requirements</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Baseline Monitoring</h4>
                    <p className="text-gray-600">Establish pre-project air quality and environmental conditions</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Permit Application</h4>
                    <p className="text-gray-600">Prepare and submit comprehensive permit applications to ADEC</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Operational Support</h4>
                    <p className="text-gray-600">Ongoing compliance monitoring and reporting assistance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-purple-50 p-8 rounded-lg max-w-4xl mx-auto border-2 border-purple-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Why Choose Raven for Geothermal Permitting?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Our team brings specialized expertise in Alaska's unique geothermal development challenges, combining deep regulatory knowledge with practical field experience.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We understand the importance of renewable energy development for Alaska's future and work to streamline the permitting process while ensuring full environmental compliance.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Advance Your Geothermal Project?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us for expert assistance with geothermal test well permitting and environmental compliance.
          </p>
          <Link
            to="/contact/geothermal"
            className="inline-flex items-center px-8 py-4 bg-white text-purple-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Start Your Geothermal Inquiry
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GeothermalPermitting;
